import React from 'react';
import _ from 'underscore';

import {Selector} from 'notebook/ELNConstants';
import {SimpleTableEditableProps} from 'notebook/simple-tables/SimpleTableEditable';
import {FullTableMap} from 'notebook/tables/excess/Evaluator';
import {
  ComponentProps,
  EnhancedProps,
  ExternalProps,
} from 'notebook/tables/spreadsheets/withSpreadsheetEvaluation';
import * as TableConstants from 'notebook/tables/TableConstants';

export interface SimpleSpreadsheetEvaluationProps {
  makeFullTableMap: ({table}: {table: TableConstants.Table}) => FullTableMap;
  selectorsFromProps: (props: SimpleTableEditableProps) => Array<Selector>;
  spreadsheetRoot: (props: SimpleTableEditableProps) => TableConstants.Table;
}

/**
 * Optimize table for spreadsheet evaluation.
 *
 * withSimpleSpreadsheet has performance optimization where it checks for deep equality of the old table and
 * new table. However, in many cases the table also includes rendering information (JSX), which
 * is not guaranteed to be the same. Since this information does not have an impact on computation,
 * we simply strip it out.
 *
 * TODO(BNCH-4552): The evaluator should not take in a full table object, but only the essentials for
 *   performing evaluation (rowData, table, columnConstraints).
 */
function prepareForComputation(table: TableConstants.Table): TableConstants.Table {
  if (table._rowHeader) {
    // eslint-disable-next-line no-restricted-syntax
    table = _.omit(table, '_rowHeader');
  }
  if (table.rowLabels) {
    // eslint-disable-next-line no-restricted-syntax
    table = _.omit(table, 'rowLabels');
  }
  return table;
}

/**
 * withSimpleSpreadsheetEvaluation is a React HOC which injects a few
 * props into the WrappedComponent so `withSpreadsheetEvaluation' can
 * properly evaluate the component.  The assumption anytime you are
 * using withSimpleSpreadsheetEvaluation is that you just have a
 * single table in your component and there are no selectors
 * enabled.  Given those constraints, the following props injected:
 *
 * - makeFullTableMap - creates a TableMap from the single Table in the props
 * - selectorsFromProps - always returns an empty array of Selectors
 *
 * @param WrappedComponent - a React ComponentClass which has a table as a prop
 */
export default function withSimpleSpreadsheetEvaluation(
  WrappedComponent: React.ComponentClass<ComponentProps & SimpleSpreadsheetEvaluationProps>
): React.ComponentClass<
  ExternalProps & EnhancedProps & SimpleSpreadsheetEvaluationProps & SimpleTableEditableProps
> {
  class Component extends React.Component<
    ExternalProps & EnhancedProps & SimpleTableEditableProps & SimpleSpreadsheetEvaluationProps
  > {
    makeFullTableMap = ({table}: {table: TableConstants.Table}): FullTableMap => {
      if (table) {
        return {
          [table.name]: prepareForComputation(table),
        };
      } else {
        return {};
      }
    };

    selectorsFromProps = (props: SimpleTableEditableProps): Array<Selector> => [];
    spreadsheetRoot = (props: SimpleTableEditableProps): TableConstants.Table => props.table;
    getNoteRuntimeSnapshot = (props: SimpleTableEditableProps): null =>
      // SimpleSpreadsheet "evaluation" is currently done live as it does not support formulas.
      null;

    render(): JSX.Element {
      return (
        <WrappedComponent
          {...this.props}
          // if the outer component explicitly states to disable formulas, then we want that preference
          // to be passed down correctly to the wrapped component
          shouldDisableFormulas={this.props.enableFormulas === false ? () => true : undefined}
          makeFullTableMap={this.makeFullTableMap}
          selectorsFromProps={this.selectorsFromProps}
          spreadsheetRoot={this.spreadsheetRoot}
          getNoteRuntimeSnapshot={this.getNoteRuntimeSnapshot}
        />
      );
    }
  }

  return Component;
}
